import { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Box, Flex, Stack } from '../../../shared/layouts';
import MessagebarContext from '../../../../context/MessagebarContext';
import Loader from '../../../shared/Loader';
import { useTheme } from '@emotion/react';
import Header from '../../../shared/Header';
import Placeholder from '../../../shared/Placeholder';
import { useMediaQuery } from '@dnhb/node_package';
import { useTranslation } from 'react-i18next';
import Card from '../../../shared/Card';
import Button from '../../../shared/Button';
import { PureDynamicForm } from '@dnhb/functional-final-form';
import {
  CheckBoxGridField,
  CheckBoxListField,
  DateField,
  InputField,
  RadioButtonField,
  RadioGridField,
  TextAreaField,
} from '../../../shared/final-form-fields';
import AddItemBox from '../../../shared/AddItemBox';
import FormList from '../../requests/questionnaires/FormList';
import { checkAndFormatDates } from '../../../../utils/dateHelpers';
import CheckBoxField from '../../../shared/final-form-fields/CheckBoxField';
import NameHeader from './components/NameHeader';
import Collapse from './components/Collapse';
import MortgageResults from './components/MortgageResults';
import useIconLibrary from '../../../../hooks/useIconLibrary';
import { useLocation , useNavigate} from 'react-router-dom';
import SaveCalculationModal from './components/SaveCalculationModal';
import Modal from '../../../shared/Modal';
import RadioButton from "../../../shared/RadioButton";
import AutoSubmitCheckBox from "./components/AutoSubmitCheckBox";

const MaxMortgagePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { breakpoints } = useTheme();
  const isMinDesktop = useMediaQuery(
    `(min-width: ${breakpoints.ml ?? '56.25rem'})`
  );
  const { setAlertMessage, setSuccesMessage } = useContext(MessagebarContext);
  const formRef = useRef(null);
  const { MortgageIcon } = useIconLibrary();

  const { search } = useLocation();
  const urlSearchParams = new URLSearchParams(search);
  const calculationId = urlSearchParams.get('calculationId');

  const [isBroken, setIsBroken] = useState(false);
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [pageIndex, setPageIndex] = useState(0);
  const [currentFrt, setCurrentFrt] = useState(10);
  const [prefillData, setPrefillData] = useState(null);
  const [submitData, setSubmitData] = useState(null);
  const [formSpec, setFormSpec] = useState({});
  const [results, setResults] = useState(null);
  const [saveModalIsOpen, setSaveModalIsOpen] = useState(false);
  const [nhgOption, setNhgOption] = useState('without_nhg');

  const nhgOptions = [
    { label: t('Zonder NHG'), value: 'without_nhg' },
    { label: t('Met NHG'), value: 'nhg' }
  ];
  const currentResults =
    results?.frt_data?.find(frt => frt?.frt_years === currentFrt) || {};
  const nhgResultOptions = nhgOptions.filter(({ value }) => !!currentResults[value]);

  useEffect(() => {
    axios
      .get(`/tools/max-mortgage/form-data${calculationId !== null ? `/${calculationId}` : ''}`)
      .then(({ data }) => {
        const { form_spec, data: prefillData, calculation_result, calculation_frt } = data;
        setFormSpec(form_spec);
        if (calculation_frt) setCurrentFrt(calculation_frt);
        if (calculation_result) {
          setPageIndex(1);
          setResults(calculation_result);
        }
        setPrefillData(checkAndFormatDates(prefillData, false, 'dob'));
        setIsLoading(false);
      })
      .catch(() => {
        setAlertMessage();
        setIsBroken(true);
      });
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pageIndex]);

  const deleteCalculation = () => {
    axios.delete(`/calculations/${calculationId}`)
      .then(() => {
        setSuccesMessage(t('Berekening is verwijderd'));
        navigate('/calculations/overview');
      })
      .catch(() => setAlertMessage(t('Er ging iets mis')));
  }

  const ToolStates = () => (
    <>
      {isBroken && (
        <Placeholder>
          {t('Er is iets misgegaan! Probeer het later opnieuw')}
          <Flex mt={'m'} justifyContent={'center'}>
            <Button
              variant={'primary-outline'}
              onClick={() => window.location.reload()}
            >
              Probeer opnieuw
            </Button>
          </Flex>
        </Placeholder>
      )}
    </>
  );

  return (
    <>
      <Stack space="m" width={{ sm: 656, ml: 672, lg: 800, xl: 1024 }}>
        {(isLoading && pageIndex !== 1) && <Loader />}
        <Header
          title={t('Maximale hypotheek')}
          icon={
            isMinDesktop ? <MortgageIcon height="2rem" width="2rem" /> : null
          }
          variant="page"
        />
        <SaveCalculationModal
          isOpen={saveModalIsOpen}
          submitData={submitData || prefillData}
          currentResults={currentResults}
          nhgResultOptions={nhgResultOptions}
          onRequestClose={() => setSaveModalIsOpen(false)}
        />
        { isBroken ? (
          <ToolStates />
        ) : (
          <>
            {pageIndex === 1 && (
              <MortgageResults {...{ results, currentFrt, setCurrentFrt, isLoading }} />
            )}
            {formSpec.questions && (
              <PureDynamicForm
                ref={formRef}
                axiosConfig={{
                  transformRequest: [
                    (data) => {
                      const input = { ...data, pageIndex };
                      setIsLoading(true);
                      setSubmitData(input);
                      return input;
                    },
                    ...axios.defaults.transformRequest,
                  ],
                }}
                onErrors={() => setIsLoading(false)}
                formDefinition={formSpec.questions}
                initialValues={prefillData}
                url={`/tools/max-mortgage/calculate`}
                afterSubmit={({ calculation_result, pageIndex }) => {
                  setResults(calculation_result);
                  setPageIndex(index => {
                    const newIndex = pageIndex || index + 1;
                    const totalPages = formSpec?.questions?.length;
                    return newIndex < totalPages ? newIndex : index;
                  });
                  setIsLoading(false);
                }}
                elementMap={{
                  text: InputField,
                  number: (props) => (
                    <InputField {...props} inputMode="numeric" />
                  ),
                  currency: (props) => <InputField {...props} isCurrency />,
                  date: DateField,
                  textarea: TextAreaField,
                  checkbox: (props) => (
                    <CheckBoxField {...props} title={props?.label} />
                  ),
                  'auto-submit-checkbox': AutoSubmitCheckBox,
                  'checkbox-list': CheckBoxListField,
                  'checkbox-grid': CheckBoxGridField,
                  radio: (props) => <RadioButtonField {...props} />,
                  'radio-grid': RadioGridField,
                  page: (props) => {
                    if (props?.name === `page_${pageIndex}`) {
                      if (props?.renderAs === 'card') {
                        return (
                          <Card>
                            <Stack {...props} space={'l'} />
                          </Card>
                        );
                      }
                      if (props?.renderAs === 'collapse') {
                        return (
                          <Collapse title={props?.title}>
                            <Stack {...props} mt={'s'} space={'s'} />
                          </Collapse>
                        );
                      }
                      return <Stack {...props} space={'l'} />;
                    }
                    return null;
                  },
                  header: NameHeader,
                  AddButton: (props) => (
                    <AddItemBox title={`${props?.item} toevoegen`} {...props} />
                  ),
                  list: (props) => <FormList {...props} />,
                }}
              />
            )}

            {/* Footer - buttons */}
            <Flex
              flexDirection={{ mobile: 'column', tablet: 'row' }}
              css={({ space }) => ({
                gap: space.m,
                '> button': {
                  width: !isMinDesktop ? '100%' : 'auto',
                }
              })}
            >
              {pageIndex === 1 && calculationId === null && (
                <Button
                  variant="primary"
                  type="button"
                  onClick={() => {
                    setSaveModalIsOpen(true);
                  }}
                >
                  {t('Sla berekening op')}
                </Button>
              )}
              {pageIndex === 1 && calculationId === null && (
                <Button
                  variant="primary-outline"
                  type="button"
                  onClick={() => setPageIndex(0)}
                >
                  {t('Pas uitgangspunten aan')}
                </Button>
              )}
              {pageIndex === 1 && calculationId !== null && (
                <Button
                  variant="primary"
                  type="button"
                  onClick={() => {
                    setDownloadModalOpen(true);
                  }}
                >
                  {t('Download deze berekening')}
                </Button>
              )}
              {pageIndex === 1 && calculationId !== null && (
                <Button
                  variant="primary-outline"
                  type="button"
                  onClick={deleteCalculation}
                >
                  {t('Verwijder berekening')}
                </Button>
              )}
              {pageIndex === 0 && (
                <Button
                  type="button"
                  onClick={() => formRef.current.requestSubmit()}
                >
                  {t('Bereken')}
                </Button>
              )}
            </Flex>
          </>
        )}
      </Stack>
      <Modal
        isOpen={downloadModalOpen}
        onRequestClose={() => setDownloadModalOpen(false)}
      >
        <Stack space={'m'}>
          <Box fontScale={'l'} fontWeight={'bold'}>
            {t('Welke versie van deze berekening wil je downloaden')}
          </Box>
          <RadioButton
            name={'ngh_option'}
            value={nhgOption}
            onChange={value => setNhgOption(value)}
            options={nhgResultOptions}
          />
          <Flex
            pt={'m'}
            flexDirection={{ mobile: 'column', desktop: 'row' }}
            justifyContent={'flex-end'}
            css={({ space }) => ({
              gap: space?.m,
              '> button': {
                width: !isMinDesktop ? '100%' : 'auto',
              },
            })}
          >
            <Button
              variant={'primary'}
              onClick={() => window.open(`/calculations/${calculationId}/pdf?nhg=${nhgOption}&frt=${currentFrt}`)}
            >
              {t('Downloaden')}
            </Button>
            <Button
              variant={'primary-outline'}
              onClick={() => setDownloadModalOpen(false)}
            >
              {t('Annuleren')}
            </Button>
          </Flex>
        </Stack>
      </Modal>
    </>
  );
};

export default MaxMortgagePage;
